import { useEffect, useState } from "react";
import ConnectionRetry from "../ConnectionRetry";
import { useNavigate } from "react-router-dom";
import { useParams, useLocation } from "react-router-dom";
import { useSite } from "../../store/useSite";
import { useAuth } from "../../store/useAuth";
import { useBase } from "../../store/useBase";
import { useSiteMeta } from "../../store/useSiteMeta";
import { getEventData, getEventId } from "../../services/event";
import {
  addSiteVisitAnalytics,
  createSite,
  getHideData,
  getSiteData,
} from "../../services/site";
import { getSpeakerData } from "../../services/speaker";
import { getSponsorData } from "../../services/sponsor";
import { authUsingEventIdAndUser, clearSiteUserSession, getSiteAuthUser } from "../../services/auth";
import { addAffiliateVisit } from "../../services/affiliate";
import data from "./data/data.json";
import AttendeeMobileNav from "./dashboard/AttendeeMobileNav";

const SiteDataProvider = ({ children }) => {
  const [loadError, setLoadError] = useState(false);
  let userDetails = useAuth.getState().user;
  const {
    siteData,
    setSiteData,
    siteId,
    setSiteId,
    setMode,
    setEventData,
    setSpeakerData,
    setSponsorData,
    setSocialData,
    setUser,
    setLinksData,
    setWhyData,
    setCardsData,
    setCookieStatus,
    setSiteUser,
    setSiteTheme,
    setTicketTheme,
    setTicketThemeMobile,
    setTicketThemeDesktop,
    setTicketHeaderTheme,
    setTicketHeaderThemeMobile,
    setTicketHeaderThemeDesktop,
    setSiteHideData,
    setSiteStyle,
    setSiteUserLoading,
  } = useSite();
  const { eventId, setEventId, setSiteSlug } = useBase();
  const { setSpeakerMeta, setSponsorMeta, setVenueMeta, setContactMeta } =
    useSiteMeta();
  let { slug, mode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  if (slug === "" || !slug) {
    slug = "registration-will-be-open-soon";
  }

  // prosses data here
  const replaceIdInSiteData = (obj, oldId, newId) => {
    if (typeof obj === "string") {
      return obj.replace(new RegExp(`\\b${oldId}\\b`, "g"), newId);
    } else if (typeof obj === "number") {
      return obj === oldId ? newId : obj;
    } else if (Array.isArray(obj)) {
      return obj.map((item) => replaceIdInSiteData(item, oldId, newId));
    } else if (obj && typeof obj === "object") {
      return Object.fromEntries(
        Object.entries(obj).map(([k, v]) => [
          k,
          replaceIdInSiteData(v, oldId, newId),
        ]),
      );
    }
    return obj;
  };

  // get any ref user
  const getAffiliateCookie = async () => {
    const name = "affiliate=";
    const decoded = decodeURIComponent(document.cookie);
    const parts = decoded.split(";");
    for (let part of parts) {
      part = part.trim();
      if (part.indexOf(name) === 0) {
        return part.substring(name.length, part.length);
      }
    }
    return null;
  };

  useEffect(() => {
    setMode(mode);
    setSiteSlug(slug);

    if (slug) {
      try {
        const cached = localStorage.getItem(`qe_ticket_theme_${slug}`);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed.ticketTheme) setTicketTheme(parsed.ticketTheme);
          if (parsed.ticketHeaderTheme) setTicketHeaderTheme(parsed.ticketHeaderTheme);
          if (parsed.ticketThemeMobile) setTicketThemeMobile(parsed.ticketThemeMobile);
          if (parsed.ticketThemeDesktop) setTicketThemeDesktop(parsed.ticketThemeDesktop);
          if (parsed.ticketHeaderThemeMobile) setTicketHeaderThemeMobile(parsed.ticketHeaderThemeMobile);
          if (parsed.ticketHeaderThemeDesktop) setTicketHeaderThemeDesktop(parsed.ticketHeaderThemeDesktop);
        }
      } catch (e) {}
    }

    const fetchEventId = async () => {
      setLoadError(false);
      if (mode !== "edit" && slug) {
        try {
          const res = await getEventId(slug);
          if (res.status === 200) {
            const data = res.data;
            if (data && typeof data === "object" && data.latestSlug) {
              const pathParts = location.pathname.split("/").filter(Boolean);
              const modeSegment = pathParts[2];
              const redirectPath = modeSegment
                ? `/${data.latestSlug}/${modeSegment}`
                : `/${data.latestSlug}`;
              navigate(redirectPath, { replace: true });
              return;
            }
            setEventId(data);
          } else if (res.status === 404) {
            navigate("/404", { replace: true });
          } else {
            setLoadError(true);
          }
        } catch (err) {
          console.error(err);
          if (err.response?.status === 404) {
            navigate("/404", { replace: true });
          } else {
            setLoadError(true);
          }
        }
      } else if (mode === "edit") {
        setEventId(slug);
      }
    };

    const RESERVED_SITE_MODES = [
      "edit", "signin", "speaker", "venue", "sponsor", "tickets", "ticket",
      "team", "agenda", "terms", "about", "contact", "dashboard",
    ];

    const setUpAffiliate = async () => {
      if (!RESERVED_SITE_MODES.includes(mode)) {
        const refUser = mode;
        const exAffiliate = await getAffiliateCookie();

        // first visit
        if (!exAffiliate && mode) {
          const now = new Date();
          now.setTime(now.getTime() + 7 * 24 * 60 * 60 * 1000);
          document.cookie = `affiliate=${refUser}; expires=${now.toUTCString()}; path=/`;

          // save visit
          await addAffiliateVisit(refUser);
        }

        // later visit or without any ref
        if (!exAffiliate && !mode) {
          return;
        }

        // return visit
        if (!mode && exAffiliate) {
          // dont save just use in buy tickets,
          // so return
          return;
        }

        // new affiliate link
        if (mode && mode !== exAffiliate) {
          const now = new Date();
          now.setTime(now.getTime() + 7 * 24 * 60 * 60 * 1000);
          document.cookie = `affiliate=${refUser}; expires=${now.toUTCString()}; path=/`;

          // save visit
          await addAffiliateVisit(refUser);
        }
      }
    };

    fetchEventId();
    setUpAffiliate();
  }, [mode, slug, navigate]);

  useEffect(() => {
    if (!slug) return;
    if (!eventId) return;

    const fetchAuth = async () => {
      // get user login data for edit mode or not edit mode
      if (mode === "edit") {
        // in edit mode
        const { token, user } = userDetails || {};

        if (!token || !user?.role_id) {
          setUser(null);
          return;
        }

        // check with token and user id, event permision
        const res = await authUsingEventIdAndUser(token, eventId);
        if (res.status === 200) {
          setUser(res.data);
          setSiteUserLoading(false);
          return;
        } else {
          setSiteUserLoading(false);
          navigate(`/site/${slug}`);
        }
      } else {
        // TODO
        // non edit mode (already done in someware so now no need to do any things here)
      }
    };

    const fetchData = async () => {
      let siteRes;
      try {
        if (!siteData) {
          // get hide data for site
          const hideRes = await getHideData(eventId);
          if (hideRes?.status === 200) {
            setSiteHideData(hideRes?.data);
          }

          siteRes = await getSiteData(eventId);

          if (siteRes.status === 200) {
            setSiteData(siteRes.data?.data);
            setSiteStyle(siteRes.data?.settings);
            setSiteId(siteRes.data?.id);
          } else if (siteRes.status === 404 && mode === "edit") {
            // prosses data for new site
            const updatedData = await replaceIdInSiteData(
              data,
              250,
              Number(eventId),
            );

            // create site
            siteRes = await createSite(
              eventId,
              userDetails?.token,
              updatedData,
            );
            setSiteData(siteRes.data?.data);
            setSiteStyle(siteRes.data?.settings);
            setSiteId(siteRes.data?.id);
          } else {
            console.warn("Unexpected status:", siteRes.status);
          }
        }

        // set site theme
        const configItem = siteRes?.data?.data[0];
        const siteTheme = configItem?.theme || "modern";
        const ticketTheme = configItem?.ticketTheme || "modern";
        const ticketHeaderTheme = configItem?.ticketHeaderTheme || "modern";
        const ticketThemeMobile =
          configItem?.ticketThemeMobile || ticketTheme || "modern";
        const ticketThemeDesktop =
          configItem?.ticketThemeDesktop || ticketTheme || "modern";
        const ticketHeaderThemeMobile =
          configItem?.ticketHeaderThemeMobile || ticketHeaderTheme || "modern";
        const ticketHeaderThemeDesktop =
          configItem?.ticketHeaderThemeDesktop || ticketHeaderTheme || "modern";
        
        setSiteTheme(siteTheme);
        setTicketTheme(ticketTheme);
        setTicketHeaderTheme(ticketHeaderTheme);
        setTicketThemeMobile(ticketThemeMobile);
        setTicketThemeDesktop(ticketThemeDesktop);
        setTicketHeaderThemeMobile(ticketHeaderThemeMobile);
        setTicketHeaderThemeDesktop(ticketHeaderThemeDesktop);

        try {
          if (slug) {
            localStorage.setItem(
              `qe_ticket_theme_${slug}`,
              JSON.stringify({
                ticketTheme,
                ticketHeaderTheme,
                ticketThemeMobile,
                ticketThemeDesktop,
                ticketHeaderThemeMobile,
                ticketHeaderThemeDesktop,
              }),
            );
          }
        } catch (e) {}

        // fetch event data
        const eventRes = await getEventData(eventId, userDetails?.token);
        setEventData(eventRes.data?.data?.event);

        // fetch speaker data
        const speakerPosition =
          eventRes.data?.data?.event?.speaker_position || [];
        const speakerRes = await getSpeakerData(eventId);

        // fetch sponsor data
        const sponsorPosition =
          eventRes.data?.data?.event?.sponsor_position || [];
        const sponsorRes = await getSponsorData(eventId);

        const orderByPosition = (
          items,
          positions,
          type,
          idField,
          positionIdField,
        ) => {
          if (!Array.isArray(items) || items.length === 0) return [];
          if (!Array.isArray(positions) || positions.length === 0) return items;

          const positionMap = new Map(
            positions
              .filter(
                (p) =>
                  p &&
                  (p.type === type || typeof p.type === "undefined") &&
                  typeof p[positionIdField] !== "undefined",
              )
              .map((p) => [p[positionIdField], p.index]),
          );

          const ordered = [...items]
            .map((item) => ({
              ...item,
              order: positionMap.has(item[idField])
                ? positionMap.get(item[idField])
                : 9999,
            }))
            .sort((a, b) => a.order - b.order)
            .map(({ order, ...rest }) => rest);

          return ordered;
        };

        // final set flow (india style order)
        const orderedSpeakers = orderByPosition(
          speakerRes.data,
          speakerPosition,
          "speaker",
          "id",
          "speakerId",
        );
        setSpeakerData(orderedSpeakers);

        const orderedSponsors = orderByPosition(
          sponsorRes.data,
          sponsorPosition,
          "sponsor",
          "id",
          "sponsorId",
        );
        setSponsorData(orderedSponsors);

        // set social data
        setSocialData(siteRes.data?.data[0]?.social);

        // set footer links , terms and policies
        const footer = siteRes.data?.data?.find(
          (item) => item.category === "Footer",
        );
        setLinksData(footer?.linksData || []);

        // set why meta data
        const whyMetaData = siteRes.data?.data?.find(
          (item) => item.category === "Why",
        );
        const whyData = {
          title: whyMetaData?.metaData?.title,
          description: eventRes.data?.data?.event?.description || "",
        };
        setWhyData(whyData);

        // set cards section data
        const cardsMetaData = siteRes.data?.data?.find(
          (item) => item.category === "Cards",
        );
        if (cardsMetaData?.metaData) {
          setCardsData(cardsMetaData.metaData);
        }

        // set speaker meta data
        const speakerMetaData = siteRes.data?.data?.find(
          (item) => item?.category === "Speaker",
        );
        setSpeakerMeta({
          title: speakerMetaData?.metaData?.title,
          description: speakerMetaData?.metaData?.description || "",
        });

        // set sponsor meta data
        const sponsorMetaData = siteRes.data?.data?.find(
          (item) => item?.category === "Sponsor",
        );
        setSponsorMeta({
          title: sponsorMetaData?.metaData?.title,
          description: sponsorMetaData?.metaData?.description || "",
        });

        // set venue meta data
        const venueMetaData = siteRes.data?.data?.find(
          (item) => item?.category === "Venue",
        );
        setVenueMeta({
          title: venueMetaData?.metaData?.title,
          description: venueMetaData?.metaData?.description || "",
        });

        // set venue meta data
        const contactMetaData = siteRes.data?.data?.find(
          (item) => item?.category === "Contact",
        );
        setContactMeta({
          title: contactMetaData?.metaData?.title,
          description: contactMetaData?.metaData?.description || "",
        });

        // set cookies
        if (mode !== "edit") {
          const status = localStorage.getItem("site_cookies");
          if (status) {
            setCookieStatus(status);
          }
        }

        // set favicon in tab when available
        const headerData = siteRes.data?.data?.find(
          (item) => item.category === "Header",
        );
        if (headerData?.favicon) {
          const link =
            document.querySelector("link[rel='icon']") ||
            document.createElement("link");
          link.rel = "icon";
          link.href = headerData?.favicon;
          document.head.appendChild(link);
        }

        // site auth
        if (mode !== "edit") {
          const res = await getSiteAuthUser(eventId);
          if (res.status === 200) {
            setSiteUser(res.data);
          } else if (res.status === 401) {
            setSiteUser(null);
            await clearSiteUserSession(eventId);
          } else if (res.status >= 500 || res.status === 408 || res.status === 429) {
            setLoadError(true);
          }
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setLoadError(true);
      }
    };

    fetchAuth();
    fetchData().finally(() => setSiteUserLoading(false));
    return () => {
      setEventData(null);
      setSiteData(null);
      setSiteStyle(null);
      setSiteUser(null);
      setCookieStatus(null);
      setWhyData(null);
      setCardsData(null);
      setLinksData(null);
      setSocialData(null);
      setSponsorData(null);
      setSpeakerData(null);
      setSiteTheme(null);
    };
  }, [eventId]);

  // site analytics page wise
  useEffect(() => {
    if (mode === "edit" || !eventId || !siteId) return;
    const visitKey = `qe_site_visit_recorded:${eventId}:${siteId}`;
    if (sessionStorage.getItem(visitKey)) return;
    const pathParts = location.pathname.split("/").filter(Boolean);
    const page = pathParts[2] || "home";
    sessionStorage.setItem(visitKey, "1");
    addSiteVisitAnalytics(eventId, page, siteId).catch(() => {
      sessionStorage.removeItem(visitKey);
    });
  }, [mode, eventId, siteId]);

  if (loadError) return <ConnectionRetry />;

  return (
    <>
      {children}
      <AttendeeMobileNav />
    </>
  );
};

export default SiteDataProvider;
